import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
  faCheckCircle, faCircleXmark, faClock, faLock, faPencil,
} from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Heading, LoadingModule, SortableList,
} from '@domain-group/suit-theme'

import useCampaignQuoteSections from '@hooks/useCampaignQuoteSections'

import { formatEntityCount } from '@functions/entity'

import useBrand from '@hooks/useBrand'
import useCampaign from '@hooks/useCampaign'

import type { CampaignQuoteModel } from '@models/types'

import SimpleNoticeBox from '@components/SimpleNoticeBox/SimpleNoticeBox'
import ApproveQuoteButton from '../ApproveQuoteButton'
import CampaignQuoteLocked from '../CampaignQuoteLocked'
import CampaignQuoteSection from '../CampaignQuoteSection'
import ContributionsRequiredNotice from '../ContributionsRequiredNotice'
import FooterActions from '../FooterActions'
import PaymentDetailsFooter from '../PaymentDetailsFooter'
import QuoteOptionsMenu from '../QuoteOptionsMenu'
import RecipientDetails from '../RecipientDetails'
import QuoteBanner from './QuoteBanner'

import useQuoteBuilder from '../../hooks/useQuoteBuilder'

type InvalidItems = CampaignQuoteModel['invalidItems']

interface CampaignQuoteProps {
  quoteBuilderPayload: ReturnType<typeof useQuoteBuilder>,
}

const CampaignQuote = (props: CampaignQuoteProps) => {
  const {
    quoteBuilderPayload,
    quoteBuilderPayload: {
      callbacks: {
        modifySectionItemSort,
      },
      campaignQuotePayload,
      campaignQuotePayload: {
        callbacks: {
          addContribution,
          bulkUpdateInvalidItems,
        },
        campaign,
        campaignQuote: {
          id,
          locked,
          title,
        },
        campaignQuoteTemplate,
        contributionsDifference,
        contributionsSufficient,
        isApproved,
        isAwaitingReview,
        isVoid,
        loading,
        requiresContributions,
        userCanApproveQuotes,
        userCanManageQuotes,
      },
      sortedSectionItemIds,
    },
  } = props

  const campaignQuoteSectionsPayload = useCampaignQuoteSections({
    filters: {
      campaign_quote_id: id,
    },
  })

  const {
    filteredCampaignQuoteSections,
    filteredCampaignQuoteSectionIds,
    lastSectionIndex,
  } = campaignQuoteSectionsPayload

  const invalidItems = (campaignQuotePayload?.campaignQuote.invalidItems ?? []) as InvalidItems

  const shouldDisplayQuoteSections = !isApproved
  && userCanManageQuotes && (isAwaitingReview ? userCanApproveQuotes : true)

  const { agency, isPropertyMarketing, vendorAmlComplete } = useCampaign(campaign)

  const brandPayload = useBrand(
    { id: agency?.brand_id },
    { performHttpRequests: !!agency?.brand_id },
  )

  const { compliantWithAML } = brandPayload

  if (loading || !title){
    return <LoadingModule loading times={3} />
  }

  return (
    <>
      {!isApproved && requiresContributions && !contributionsSufficient && (
        <ContributionsRequiredNotice
          callbacks={{ addContribution }}
          contributionsDifference={contributionsDifference}
        />
      )}

      {/* VendorAMLComplete ≠ true (i.e. false, null, or absent). to be added */}
      {!!compliantWithAML && isPropertyMarketing && !vendorAmlComplete && (
        <SimpleNoticeBox
          title="Quote approval unavailable due to AML compliance requirements"
          paragraph="Quote approval is unavailable until AML compliance has been confirmed for this vendor."
        />
      )}

      <Box flexDirection="column" marginBottom="large" variant="white">
        <Box
          alignItems={['flex-start', 'center']}
          borderBottom="1px solid"
          borderColor="lineColor"
          flexDirection={['column', 'row']}
          padding="large"
        >
          <Heading size="large" textProps={{ lineHeight: '1.3' }}>
            {title || 'Loading...'}
          </Heading>

          <Box
            borderTop={['1px dashed', 'none']}
            borderColor="lineColor"
            justifyContent="flex-end"
            marginTop={['large', 0]}
            paddingTop={['large', 0]}
            marginLeft="auto"
            width={['100%', 'auto']}
          >
            <QuoteOptionsMenu campaignQuotePayload={campaignQuotePayload} locked={locked} />

            {!!invalidItems?.length && !isApproved && (
              <Button
                buttonStyle="secondaryEdit"
                icon={<FontAwesomeIcon icon={faPencil} />}
                onClick={() => bulkUpdateInvalidItems()}
                marginLeft="medium"
                // @ts-expect-error TS2322
                size={['medium', 'small']}
                width="fit-content"
              >
                {`Update ${formatEntityCount({ entityString: 'Price', entityCount: invalidItems?.length })}`}
              </Button>
            )}

            <ApproveQuoteButton
              campaignQuotePayload={campaignQuotePayload}
              compliantWithAML={compliantWithAML}
            />
          </Box>
        </Box>

        <RecipientDetails quoteBuilderPayload={quoteBuilderPayload} />

        {shouldDisplayQuoteSections && (
          // @ts-expect-error TS2746
          <SortableList
            callbacks={{
              onDragEnd: (fromIndex, toIndex, dragDropPayload) => modifySectionItemSort({
                // @ts-expect-error TS2322
                dragDropPayload,
                fromIndex,
                // @ts-expect-error TS2322
                toIndex,
              }),
            }}
          >
            {locked && (
              <CampaignQuoteLocked />
            )}

            {!locked && filteredCampaignQuoteSections.map((campaignQuoteSection, index) => {
              const { id } = campaignQuoteSection
              // @ts-expect-error TS7053
              const sortedItemIds = sortedSectionItemIds[`Section-${id}`] || []

              return (
                <CampaignQuoteSection
                  campaignQuoteSection={campaignQuoteSection}
                  // @ts-expect-error TS2322
                  campaignQuoteSectionIds={filteredCampaignQuoteSectionIds}
                  // @ts-expect-error TS2739
                  campaignQuoteTemplate={campaignQuoteTemplate}
                  first={index === 0}
                  index={index}
                  key={campaignQuoteSection.id}
                  last={lastSectionIndex === index}
                  lastIndex={lastSectionIndex}
                  sortedCampaignQuoteItemIds={sortedItemIds}
                />
              )
            })}
          </SortableList>
        )}

        {!userCanApproveQuotes && isAwaitingReview && (
          <QuoteBanner
            displayText="This Quote is awaiting review and cannot be modified."
            icon={faClock}
            iconColor="orange"
          />
        )}

        {(isApproved || isVoid) && (
          <QuoteBanner
            displayText={`This Quote has been ${isVoid ? 'Voided' : 'Approved'} and cannot be modified.`}
            icon={isVoid ? faCircleXmark : faCheckCircle}
            iconColor={isVoid ? 'red' : 'green'}
          />
        )}

        {!userCanManageQuotes && !isApproved && !isVoid && (
          <QuoteBanner
            displayText="Your user permissions do not allow for managing quotes."
            icon={faLock}
            iconColor="orange"
          />
        )}

        <PaymentDetailsFooter campaignQuotePayload={campaignQuotePayload} />

        <FooterActions
          campaignQuotePayload={campaignQuotePayload}
          compliantWithAML={compliantWithAML}
          locked={locked}
        />
      </Box>
    </>
  )
}

export default CampaignQuote
